import AuthService from './AuthService';
import RequestBuilder from '../Lib/Requests';
import {
  EPEC_6200_VENDOR_ID,
  ACTIA_ACU6_VENDOR_ID,
} from '../Constants/Vendors';

export default class TemplateService {
  auth = new AuthService();
  api = new RequestBuilder();

  buildTemplateCountForOrgRequest(orgID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: '/v2/template',
      method: 'get',
      params: {
        orgID,
        limit: 0,
      },
      cancelToken,
    };
  }
  buildPaginatedTemplatesRequest(
    page,
    orgID,
    limit = 100,
    sort = 'name',
    order = 'asc',
    search,
    vendor,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: '/v2/template',
      method: 'get',
      params: {
        orgID,
        page,
        limit,
        sort,
        order,
        search,
        vendor,
      },
      cancelToken,
    };
  }
  async fetchTemplateCountForOrg(
    orgID = this.auth.getCurrentOrgID(),
    cancelToken
  ) {
    const { data = {} } = await this.api.request(
      this.buildTemplateCountForOrgRequest(orgID, cancelToken)
    );
    return data;
  }
  async fetchPaginatedTemplates(
    page = 1,
    orgID = this.auth.getCurrentOrgID(),
    limit,
    sort,
    order,
    search,
    vendor,
    cancelToken
  ) {
    let vendorInUse;
    if (vendor === EPEC_6200_VENDOR_ID || vendor === ACTIA_ACU6_VENDOR_ID) {
      vendorInUse = 'elevat';
    } else {
      vendorInUse = vendor;
    }

    const { data = {} } = await this.api.request(
      this.buildPaginatedTemplatesRequest(
        page,
        orgID,
        limit,
        sort,
        order,
        search,
        vendorInUse,
        cancelToken
      )
    );
    return data;
  }

  buildAdditionalPaginatedTemplatesRequest(
    { order, orgID, page, search, sort = 'name', vendor },
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: '/v2/template',
      method: 'get',
      params: {
        limit: 100,
        order,
        orgID,
        page,
        search,
        sort,
        vendor,
      },
      cancelToken,
    };
  }
  async handleAdditionalTemplateRequests({
    cancelToken,
    count,
    limit,
    order,
    orgID,
    search,
    sort,
    vendor,
  }) {
    const requestsMap = this.api.createAdditionalRequestMap(
      { count, limit, order, orgID, search, sort, vendor },
      this.buildAdditionalPaginatedTemplatesRequest,
      cancelToken
    );
    return Promise.all(requestsMap);
  }

  buildAssignTemplateRequest(gatewayID, templateID, data, cancelToken) {
    return {
      cancelToken,
      data,
      endpoint: `/gateway/${gatewayID}/template/${templateID}`,
      method: 'post',
      service: 'gateway',
    };
  }
  async assignTemplate(assetID, templateID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildAssignTemplateRequest(assetID, templateID, body, cancelToken)
    );
    return data;
  }

  buildCreateNewTemplateRequest(orgID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template`,
      method: 'post',
      params: { orgID },
      data,
      cancelToken,
    };
  }
  async createNewTemplate(orgID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildCreateNewTemplateRequest(orgID, body, cancelToken)
    );
    return data;
  }

  buildFetchTemplateRequest(templateID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}`,
      method: 'get',
      cancelToken,
    };
  }
  async fetchTemplate(templateID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildFetchTemplateRequest(templateID, cancelToken)
    );
    return data;
  }

  buildCheckTemplateRequest(templateID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/check`,
      method: 'get',
      cancelToken,
    };
  }
  async checkTemplate(templateID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildCheckTemplateRequest(templateID, cancelToken)
    );
    return data;
  }

  buildFetchAllTemplatesRequest(orgID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: '/template/all',
      method: 'get',
      params: { orgID },
      cancelToken,
    };
  }
  async fetchAllTemplates(orgID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildFetchAllTemplatesRequest(orgID, cancelToken)
    );
    return data;
  }

  buildEditTemplateRequest(templateID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}`,
      method: 'put',
      data,
      cancelToken,
    };
  }
  async editTemplate(templateID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildEditTemplateRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildDeleteTemplateRequest(templateID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}`,
      method: 'delete',
      cancelToken,
    };
  }
  async deleteTemplate(templateID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildDeleteTemplateRequest(templateID, cancelToken)
    );
    return data;
  }

  buildDuplicateTemplateRequest(templateID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/clone`,
      method: 'post',
      data,
      cancelToken,
    };
  }
  async duplicateTemplate(templateID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildDuplicateTemplateRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildTemplateSignalCreateRequest(templateID, datapointID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint/${datapointID}/value`,
      method: 'post',
      data,
      cancelToken,
    };
  }
  async addSignalToCollection(templateID, datapointID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildTemplateSignalCreateRequest(
        templateID,
        datapointID,
        body,
        cancelToken
      )
    );
    return data;
  }

  buildTemplateSignalDeleteRequest(
    templateID,
    datapointID,
    signalID,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint/${datapointID}/value/${signalID}`,
      method: 'delete',
      cancelToken,
    };
  }
  async deleteSignal(templateID, datapointID, signalID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildTemplateSignalDeleteRequest(
        templateID,
        datapointID,
        signalID,
        cancelToken
      )
    );
    return data;
  }

  buildUpdateSignalRequest(
    templateID,
    collectionID,
    signalID,
    data,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint/${collectionID}/value/${signalID}`,
      method: 'put',
      data,
      cancelToken,
    };
  }
  async updateSignal(templateID, collectionID, signalID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildUpdateSignalRequest(
        templateID,
        collectionID,
        signalID,
        body,
        cancelToken
      )
    );
    return data;
  }

  buildEditSignalCollectionRequest(
    templateID,
    collectionID,
    data,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint/${collectionID}`,
      method: 'put',
      data,
      cancelToken,
    };
  }
  async editSignalCollection(templateID, collectionID, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildEditSignalCollectionRequest(
        templateID,
        collectionID,
        body,
        cancelToken
      )
    );
    return data;
  }

  buildDeleteSignalCollectionRequest(templateID, collectionID, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint/${collectionID}`,
      method: 'delete',
      cancelToken,
    };
  }
  async deleteSignalCollection(templateID, collectionID, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildDeleteSignalCollectionRequest(
        templateID,
        collectionID,
        cancelToken
      )
    );
    return data;
  }

  buildAddSignalCollectionRequest(templateID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/template/${templateID}/datapoint`,
      method: 'post',
      data,
      cancelToken,
    };
  }
  async addSignalCollection(templateID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildAddSignalCollectionRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildTemplateSearchRequest(params, cancelToken) {
    return {
      service: 'gateway',
      endpoint: '/v2/template/search',
      method: 'get',
      params,
      cancelToken,
    };
  }
  async searchTemplates(params, cancelToken) {
    const { data } = await this.api.request(
      this.buildTemplateSearchRequest(params, cancelToken)
    );
    return data;
  }

  buildCreateVendorTemplateRequest(
    { vendor = 'danfoss', ...params },
    data,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/v2/template/${vendor}`,
      method: 'post',
      params,
      data,
      cancelToken,
    };
  }
  async createVendorTemplate(params, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildCreateVendorTemplateRequest(params, body, cancelToken)
    );
    return data;
  }

  buildUpdateVendorTemplateRequest(
    templateID,
    { vendor, ...params },
    data,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/v2/template/${templateID}/${vendor}`,
      method: 'put',
      params,
      data,
      cancelToken,
    };
  }
  async updateVendorTemplate(templateID, params, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildUpdateVendorTemplateRequest(
        templateID,
        params,
        body,
        cancelToken
      )
    );
    return data;
  }

  buildCreateWaterpumpTemplateRequest(params, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/v2/template/waterPump`,
      method: 'post',
      params,
      data,
      cancelToken,
    };
  }
  async createWaterpumpTemplate(params, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildCreateWaterpumpTemplateRequest(params, body, cancelToken)
    );
    return data;
  }

  buildFetchPaginatedOutputSignalsRequest(templateID, params, cancelToken) {
    return {
      cancelToken,
      endpoint: `/v2/template/${templateID}/outputSignal`,
      method: 'get',
      params,
      service: 'gateway',
    };
  }
  async fetchPaginatedOutputSignals(gtemplateID, params, cancelToken) {
    const { data } = await this.api.request(
      this.buildFetchPaginatedOutputSignalsRequest(
        gtemplateID,
        params,
        cancelToken
      )
    );
    return data;
  }

  buildCreateOutputSignalRequest(templateID, data, params, cancelToken) {
    return {
      cancelToken,
      data,
      endpoint: `/v2/template/${templateID}/outputSignal`,
      method: 'post',
      params,
      service: 'gateway',
    };
  }
  async createOutputSignal(templateID, body, params, cancelToken) {
    const { data } = await this.api.request(
      this.buildCreateOutputSignalRequest(templateID, body, params, cancelToken)
    );
    return data;
  }

  buildUpdateOutputSignalRequest(
    templateID,
    outputSignalID,
    data,
    params,
    cancelToken
  ) {
    return {
      cancelToken,
      data,
      endpoint: `/v2/template/${templateID}/outputSignal/${outputSignalID}`,
      method: 'put',
      params,
      service: 'gateway',
    };
  }
  async updateOutputSignal(
    templateID,
    outputSignalID,
    body,
    params,
    cancelToken
  ) {
    const { data } = await this.api.request(
      this.buildUpdateOutputSignalRequest(
        templateID,
        outputSignalID,
        body,
        params,
        cancelToken
      )
    );
    return data;
  }

  buildDeleteOutputSignalRequest(templateID, outputSignalID, cancelToken) {
    return {
      cancelToken,
      endpoint: `/v2/template/${templateID}/outputSignal/${outputSignalID}`,
      method: 'delete',
      service: 'gateway',
    };
  }
  deleteOutputSignal(templateID, outputSignalID, cancelToken) {
    return this.api.request(
      this.buildDeleteOutputSignalRequest(
        templateID,
        outputSignalID,
        cancelToken
      )
    );
  }

  buildSearchOutputSignalsRequest(templateID, params, cancelToken) {
    return {
      cancelToken,
      endpoint: `/v2/template/${templateID}/outputSignalSearch`,
      method: 'get',
      params,
      service: 'gateway',
    };
  }
  async searchOutputSignals(templateID, params, cancelToken) {
    const { data } = await this.api.request(
      this.buildSearchOutputSignalsRequest(templateID, params, cancelToken)
    );
    return data;
  }

  buildFetchOutputSignalRequest(templateID, outputSignalID, cancelToken) {
    return {
      cancelToken,
      endpoint: `/v2/template/${templateID}/outputSignal/${outputSignalID}`,
      method: 'get',
      service: 'gateway',
    };
  }
  async fetchOutputSignal(templateID, outputSignalID, cancelToken) {
    const { data } = await this.api.request(
      this.buildFetchOutputSignalRequest(
        templateID,
        outputSignalID,
        cancelToken
      )
    );
    return data;
  }

  buildCreateBulkSignalsRequest(templateID, data, cancelToken) {
    return {
      service: 'gateway',
      endpoint: `/v2/template/${templateID}/createBulkSignals`,
      method: 'post',
      data,
      cancelToken,
    };
  }

  async createBulkSignals(templateID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildCreateBulkSignalsRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildCloneSignalRequest(
    templateID,
    datapointID,
    signalID,
    data,
    cancelToken
  ) {
    return {
      service: 'gateway',
      endpoint: `/v2/template/${templateID}/datapoint/${datapointID}/value/${signalID}/clone`,
      method: 'post',
      data,
      cancelToken,
    };
  }

  async cloneSignal(templateID, datapointID, signalID, body, cancelToken) {
    const { data = {} } = await this.api.request(
      this.buildCloneSignalRequest(
        templateID,
        datapointID,
        signalID,
        body,
        cancelToken
      )
    );
    return data;
  }

  buildEnableForWaterpumpRequest(templateID, cancelToken) {
    return {
      cancelToken,
      endpoint: `/template/${templateID}/waterpump`,
      method: 'put',
      service: 'gateway',
    };
  }
  async enableForWaterpump(templateID, cancelToken) {
    const { data } = await this.api.request(
      this.buildEnableForWaterpumpRequest(templateID, cancelToken)
    );
    return data;
  }

  buildUpdateSharedGtemplateRequest(templateID, data, cancelToken) {
    return {
      cancelToken,
      data,
      endpoint: `/v2/template/${templateID}/sharedGtemplate`,
      method: 'put',
      service: 'gateway',
    };
  }
  async updateSharedGtemplate(templateID, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildUpdateSharedGtemplateRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildCreateEzSignalRequest(templateID, data, cancelToken) {
    return {
      cancelToken,
      data,
      endpoint: `/v2/template/${templateID}/xirgoSignal`,
      method: 'post',
      service: 'gateway',
    };
  }
  async createEzSignal(templateID, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildCreateEzSignalRequest(templateID, body, cancelToken)
    );
    return data;
  }

  buildUpdateEzSignalRequest(templateID, signalID, data, cancelToken) {
    return {
      cancelToken,
      data,
      endpoint: `/v2/template/${templateID}/xirgoSignal/${signalID}`,
      method: 'put',
      service: 'gateway',
    };
  }
  async updateEzSignal(templateID, signalID, body, cancelToken) {
    const { data } = await this.api.request(
      this.buildUpdateEzSignalRequest(templateID, signalID, body, cancelToken)
    );
    return data;
  }

  buildDeleteEzSignalRequest(templateID, signalID, cancelToken) {
    return {
      cancelToken,
      endpoint: `/v2/template/${templateID}/xirgoSignal/${signalID}`,
      method: 'delete',
      service: 'gateway',
    };
  }
  async deleteEzSignal(templateID, signalID, cancelToken) {
    return this.api.request(
      this.buildDeleteEzSignalRequest(templateID, signalID, cancelToken)
    );
  }
}
